import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from './SEO';
import Navbar from "./layout/Navbar";
import LazyImage from "./ui/LazyImage";
import LazyVideo from "./ui/LazyVideo";
import useFavicon from '../hooks/useFavicon';
import Footer from './layout/Footer';
import FloatingActions from './layout/FloatingActions';

// Bespoke premium rotating words animation for the Student LMS Hero Heading
function RotatingHeading() {
  const words = ["LMS Platform", "Progress Tracker", "Recorded Syncs", "Concept Modules"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 bg-gradient-to-r from-[#0B1C2D] to-[#1E293B] bg-clip-text text-transparent font-extrabold tracking-tight min-w-[150px] min-[360px]:min-w-[240px] sm:min-w-[320px] md:min-w-[420px] lg:min-w-[460px]">
      {subText}
      <span className="border-r-[3.5px] border-[#0B1C2D] ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

export default function StudentLMS() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  let lastScrollY = useRef(0);

  // Update favicon for this page
  useFavicon('/logo-main.png');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Hide/show buttons based on scroll direction
      if (window.scrollY > lastScrollY.current && window.scrollY > 100) {
        // Scrolling down - hide buttons
        setIsVisible(false);
      } else if (window.scrollY < lastScrollY.current) {
        // Scrolling up - show buttons
        setIsVisible(true);
      }
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <React.Fragment>
      <SEO
        title="Cervello Student LMS – Coming Soon | Cervello Eduverse"
        description="Cervello Student LMS is launching soon. A structured learning platform designed for serious academic and career outcomes with recorded sessions, assignments, and progress tracking."
        keywords="student lms, learning management system, online learning, student portal, education platform, course management, student dashboard"
        url="https://cervelloeduverse.com/student-lms"
        image="https://cervelloeduverse.com/images/lms-og-image.jpg"
        canonical="https://cervelloeduverse.com/student-lms"
        faqData={[
          {
            question: "When is the Cervello Student LMS launching?",
            answer: "The Student LMS is launching shortly. All enrolled students of Cervello Junior Academy, Elevare, and Business Lab will receive secure login credentials automatically when their batch starts."
          },
          {
            question: "What features are available in the Cervello LMS?",
            answer: "The LMS features include recorded class lectures, assignments and daily practice sheets, concept-wise structured modules, progress tracking dashboards, and official course completion certificates."
          }
        ]}
        schemaData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Cervello Student LMS",
          "url": "https://cervelloeduverse.com/student-lms",
          "description": "A structured learning platform designed for serious academic and career outcomes.",
          "logo": "https://cervelloeduverse.com/images/cervello-logo.webp",
          "sameAs": [
            "https://www.facebook.com/cervelloeduverse",
            "https://www.instagram.com/cervelloeduverse",
            "https://www.linkedin.com/company/cervelloeduverse",
            "https://wa.me/918639619079"
          ],
          "areaServed": "IN",
          "serviceType": "Education, Online Learning Platform",
          "founder": {
            "@type": "Person",
            "name": "Mohd. Yaasir Sufiyan",
            "jobTitle": "Founder",
            "worksFor": {
              "@type": "Organization",
              "name": "Cervello Eduverse"
            }
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Santoshnagar-Saidabad Main road, 5th Floor, Emaan Manzil, Royal Embassy School Lane, beside Success Group of Institutions",
            "addressLocality": "Hyderabad",
            "addressRegion": "Telangana",
            "postalCode": "500059",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 17.3553111,
            "longitude": 78.5057216
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-86396-19079",
            "contactType": "customer service",
            "email": "cervelloeduverse@gmail.com",
            "areaServed": "IN"
          }
        }}
      />
      <div className="font-inter bg-nm-light min-h-screen text-[#1E293B]">
        {/* Navigation */}
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[90vh] flex items-center py-20 lg:py-24 bg-nm-light overflow-hidden" aria-label="Hero Section">
          {/* Responsive Brand Video Background */}
          <LazyVideo
            src={isMobile ? "/videos/home-mobile.mp4" : "/videos/home-desktop.mp4"}
            poster="/logo-main.webp"
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.25]"
            placeholderBg="bg-transparent"
          />

          {/* Background glows */}
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#87CEEB]/10 rounded-full blur-[130px] animate-sunrise-shift pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#C6A25E]/5 rounded-full blur-[120px] animate-pulse-slow pointer-events-none" style={{ animationDelay: '4s' }} />

          <div className="relative max-w-[1500px] mx-auto px-6 w-full flex flex-col items-center justify-center min-h-[80vh] relative z-20 py-20">
            {/* Centered Content Column */}
            <div className="relative z-20 text-center max-w-4xl mx-auto flex flex-col items-center animate-slide-up">
              {/* Header Badge with Logo */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <LazyImage
                  src="/logo-main.webp"
                  alt="Cervello Logo"
                  className="w-14 h-14 rounded-full object-cover border border-[#0B1C2D]/20 shadow-lg bg-white"
                  fetchPriority="high"
                />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-light text-[#0B1C2D] text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-[#0B1C2D] animate-pulse" />
                  <span>LMS Coming Soon</span>
                </div>
              </div>

              {/* Centered Heading */}
              <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-[#0B1C2D] tracking-tight mb-8">
                <span className="block">Cervello Student</span>
                <span className="block mt-2"><RotatingHeading /></span>
              </h1>

              {/* Centered Description */}
              <h2 className="text-base sm:text-lg md:text-xl text-slate-700 mb-8 leading-relaxed max-w-2xl mx-auto font-light">
                A structured learning platform designed for serious academic and career outcomes.
              </h2>

              <p className="text-sm text-slate-600 mb-10 max-w-md mx-auto">
                LMS access will be enabled during the course cycle.
              </p>

              {/* Centered CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6 w-full max-w-md">
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdBn032kGYWcaxcXSNpCw60ay5Yq5f9a5QKLSEdsbDfR91-_A/viewform?usp=dialog"
                  className="px-8 py-4.5 rounded-2xl glass-button-light text-[#0B1C2D] font-bold text-center w-full sm:w-auto"
                >
                  👉 Register Demo Session
                </a>
              </div>

              <p className="text-sm text-slate-500">
                Limited seats · Priority access for early learners
              </p>
            </div>
          </div>
        </section>

        {/* Trust Statement Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto">
              <div className="nm-flat-light p-8 rounded-2xl border-transparent hover:nm-inset-light transition-all duration-300">
                <p className="text-lg text-slate-700 leading-relaxed">
                  We are currently onboarding our first batch of students. To ensure quality learning and a smooth experience, our Student LMS is launching shortly. All enrolled students will receive secure access automatically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* LMS Features Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D] tracking-tight">
                What You'll Get Inside the <span className="bg-gradient-to-r from-[#0B1C2D] to-[#1E293B] bg-clip-text text-transparent">Cervello LMS</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Recorded Class Sessions",
                  description: "Access to all recorded lectures and sessions",
                  icon: "📹",
                  features: []
                },
                {
                  title: "Assignments & Practice Questions",
                  description: "Curated assignments and practice materials",
                  icon: "📝",
                  features: []
                },
                {
                  title: "Concept-wise Learning Modules",
                  description: "Structured learning content by concept",
                  icon: "🧠",
                  features: []
                },
                {
                  title: "Progress Tracking Dashboard",
                  description: "Track your learning progress in real-time",
                  icon: "📊",
                  features: []
                },
                {
                  title: "Course Completion Certificate",
                  description: "Official certificates upon course completion",
                  icon: "📜",
                  features: []
                },
                {
                  title: "Secure Student Login Access",
                  description: "Safe and secure access to your learning materials",
                  icon: "🔒",
                  features: []
                }
              ].map((item, index) => (
                <div key={index} className="nm-flat-light p-8 rounded-2xl border-transparent hover:-translate-y-1.5 transition-all duration-300 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="w-16 h-16 rounded-xl nm-inset-light flex items-center justify-center text-[#0B1C2D] text-2xl mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-[#0B1C2D]">{item.title}</h3>
                  <p className="text-slate-600 mb-6">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Demo-First Philosophy Section */}
        <section className="py-24 bg-nm-dark text-[#0B1C2D] border-t border-slate-200/20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto">
              <div className="nm-flat-dark p-8 rounded-2xl border-transparent hover:nm-inset-dark transition-all duration-300">
                <h3 className="font-playfair text-3xl font-bold mb-6 bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">
                  Learn first. Decide later.
                </h3>
                <p className="text-lg text-slate-700 leading-relaxed font-light">
                  Attend our demo sessions to experience our teaching approach. If you're satisfied, you can enroll. No pressure. No commitment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Founder's Quote Section */}
        <section className="py-24 bg-nm-light text-center relative overflow-hidden border-t border-slate-200/40">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-[#C6A25E] rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#87CEEB] rounded-full blur-3xl" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="text-center">
              <div className="inline-block p-8 nm-flat-light rounded-2xl mb-8 border-transparent">
                <h3 className="font-playfair text-3xl font-bold text-slate-700 tracking-tight">
                  Founder's <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">Message</span>
                </h3>
              </div>
              <div className="flex flex-col items-center">
                <LazyImage
                  src="/founder.webp"
                  alt="Mohd. Yaasir Sufiyan, Founder of Cervello Student LMS"
                  className="w-16 h-16 rounded-full object-cover mb-6 nm-flat-light"
                />
                <p className="text-xl md:text-2xl font-medium text-[#0B1C2D] max-w-4xl mx-auto italic">
                  "The Student LMS is designed to be your personalized learning companion. It's not just a platform for accessing content, but a comprehensive ecosystem that tracks your progress, provides feedback, and adapts to your learning style. We believe that technology should enhance the learning experience, making it more engaging, interactive, and effective. Our LMS is built with the understanding that every student learns differently, and our goal is to provide the tools and resources that help each learner reach their full potential."
                </p>
                <p className="text-lg font-bold text-[#0B1C2D] max-w-4xl mx-auto mt-6">
                  — <span className="italic font-serif">Mohd. Yaasir Sufiyan</span>, Founder, Cervello Student LMS
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 7-Day Demo Session Section - Reusing existing design */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D] tracking-tight">
                Try Out Our <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">Demo Sessions</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Experience our classes and curriculum structure with options tailored to your track
              </p>
            </div>

            <div className="max-w-4xl mx-auto nm-flat-light rounded-2xl overflow-hidden border-transparent">
              <div className="md:flex">
                <div className="md:w-2/5 p-6 flex items-center justify-center bg-[#f0f4f9] text-slate-800 border-r border-slate-200/30">
                  <div className="text-center space-y-4 px-2">
                    <LazyImage
                      src="/demo.webp"
                      alt="Demo Session Preview"
                      className="max-w-full h-auto rounded-xl nm-flat-light mb-4"
                    />
                    <div className="border-t border-slate-200/30 pt-3">
                      <div className="text-sm font-bold text-[#C6A25E]">ELEVARE ACADEMY</div>
                      <div className="text-2xl font-bold">₹99 <span className="text-xs font-light opacity-80">/ 7 Days Demo</span></div>
                    </div>
                    <div className="border-t border-slate-200/30 pt-3">
                      <div className="text-sm font-bold text-[#C6A25E]">JUNIOR ACADEMY</div>
                      <div className="text-2xl font-bold">FREE <span className="text-xs font-light opacity-80">/ 1 Day Demo</span></div>
                    </div>
                    <div className="border-t border-slate-200/30 pt-3">
                      <div className="text-sm font-bold text-[#C6A25E]">BUSINESS LAB</div>
                      <div className="text-2xl font-bold">FREE <span className="text-xs font-light opacity-80">/ Consultation</span></div>
                    </div>
                  </div>
                </div>

                <div className="md:w-3/5 p-12">
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-[#0B1C2D]">Complete Concept Explanation</h3>
                        <p className="text-slate-600 mt-1">Detailed explanations of all core concepts</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-[#0B1C2D]">Complete Project Development</h3>
                        <p className="text-slate-600 mt-1">Build projects from scratch to completion</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-[#0B1C2D]">Full Course Duration Experience</h3>
                        <p className="text-slate-600 mt-1">Experience the full duration and quality</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-[#0B1C2D]">Flexible Trial Formats</h3>
                        <p className="text-slate-600 mt-1">Full access tailored to your program vertical</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#C6A25E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-[#0B1C2D]">Satisfaction Guaranteed</h3>
                        <p className="text-slate-600 mt-1">Enroll in full course if satisfied</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10">
                    <a
                      href="/book-demo"
                      className="w-full nm-button-light text-[#0B1C2D] py-4 rounded-xl font-bold border-transparent block text-center active:scale-95 transition-all"
                    >
                      Book Trial or Consultation Session
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Global Footer & FABs */}
        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}